import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL, formatDate, getCertificateStatus, UserRoles } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { MoreHorizontal, Award, Search, Printer, Eye, Trash2, CalendarIcon, X, Filter, Download, Loader2, FileText } from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, isWithinInterval, parseISO } from 'date-fns';
import { SortableHeader } from '../components/SortableHeader';

const CertificatePrintView = ({ certificate, template, classData, onClose }) => {
  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      alert('Please allow popups for this site to print certificates');
      return;
    }
    
    // If we have a template with positions, use template-based printing
    if (template) {
      const orientation = template.page_orientation || 'portrait';
      const pageWidth = orientation === 'landscape' ? 297 : 210; // A4 in mm
      const pageHeight = orientation === 'landscape' ? 210 : 297;
      
      // Helper to generate positioned text
      const renderField = (fieldConfig, value) => {
        if (!fieldConfig || !fieldConfig.enabled || !value) return '';
        const { x, y, font_size, font_weight, text_align } = fieldConfig;
        return `
          <div style="
            position: absolute;
            left: ${x}mm;
            top: ${y}mm;
            font-size: ${font_size || 12}pt;
            font-weight: ${font_weight || 'normal'};
            text-align: ${text_align || 'left'};
            ${text_align === 'center' ? 'transform: translateX(-50%);' : ''}
            ${text_align === 'right' ? 'transform: translateX(-100%);' : ''}
          ">${value}</div>
        `;
      };
      
      // Helper to generate positioned image (for signature)
      const renderImage = (fieldConfig, imageUrl) => {
        if (!fieldConfig || !fieldConfig.enabled || !imageUrl) return '';
        const { x, y, font_size } = fieldConfig;
        // font_size is repurposed as image height in mm for signature
        const height = font_size ? font_size * 2 : 30; // Default 30mm height
        return `
          <img src="${imageUrl}" style="
            position: absolute;
            left: ${x}mm;
            top: ${y}mm;
            height: ${height}mm;
            object-fit: contain;
          " />
        `;
      };
      
      // Construct signature URL - signature_url already includes /api prefix
      const signatureFullUrl = classData?.signature_url 
        ? window.location.origin + classData.signature_url
        : null;
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Certificate - ${certificate.certificate_number}</title>
            <style>
              @page {
                size: ${orientation};
                margin: 0;
              }
              body {
                font-family: 'Inter', Arial, sans-serif;
                margin: 0;
                padding: 0;
              }
              .certificate-page {
                position: relative;
                width: ${pageWidth}mm;
                height: ${pageHeight}mm;
                overflow: hidden;
              }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="certificate-page">
              ${renderField(template.certificate_number, certificate.certificate_number)}
              ${renderField(template.student_name, certificate.person_name)}
              ${renderField(template.course_name, certificate.outcome_name || certificate.course_name)}
              ${renderField(template.company_name, certificate.company_name)}
              ${renderField(template.issue_date, new Date(certificate.issue_date).toLocaleDateString('en-GB'))}
              ${renderField(template.expiry_date, certificate.expiry_date ? new Date(certificate.expiry_date).toLocaleDateString('en-GB') : null)}
              ${renderField(template.instructor_names, classData?.instructor_names)}
              ${renderField(template.instructor_numbers, classData?.instructor_numbers)}
              ${renderField(template.training_center_name, classData?.training_center_name)}
              ${renderField(template.duration_hours, classData?.duration_hours ? `${classData.duration_hours} hours` : null)}
              ${renderField(template.declaration_text, classData?.declaration_text)}
              ${renderField(template.extra_text, classData?.extra_text)}
              ${renderImage(template.signature, signatureFullUrl)}
            </div>
          </body>
        </html>
      `);
    } else {
      // Default print layout (no template)
      printWindow.document.write(`
        <html>
          <head>
            <title>Certificate - ${certificate.certificate_number}</title>
            <style>
              body {
                font-family: 'Inter', sans-serif;
                padding: 40px;
                max-width: 800px;
                margin: 0 auto;
              }
              .certificate-header {
                text-align: center;
                margin-bottom: 40px;
                padding-bottom: 20px;
                border-bottom: 2px solid #2C9DE1;
              }
              .certificate-header img {
                max-height: 60px;
              }
              .certificate-field {
                margin: 20px 0;
                padding: 10px 0;
                border-bottom: 1px solid #e5e5e5;
              }
              .certificate-field label {
                font-weight: 600;
                color: #666;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              .certificate-field p {
                font-size: 18px;
                margin: 5px 0 0 0;
                color: #111;
              }
              .certificate-number {
                font-family: monospace;
                font-size: 24px;
                color: #2C9DE1;
              }
              @media print {
                body { padding: 20px; }
              }
            </style>
          </head>
          <body>
            <div class="certificate-header">
              <img src="${window.location.origin}/logo.png" alt="EMP Medic" />
              <p style="color: #666; margin-top: 10px;">Training Certificate</p>
            </div>
            <div class="certificate-field">
              <label>Certificate Number</label>
              <p class="certificate-number">${certificate.certificate_number}</p>
            </div>
            <div class="certificate-field">
              <label>Participant Name</label>
              <p>${certificate.person_name}</p>
            </div>
            <div class="certificate-field">
              <label>Course</label>
              <p>${certificate.outcome_name || certificate.course_name}</p>
            </div>
            ${certificate.company_name ? `
            <div class="certificate-field">
              <label>Company</label>
              <p>${certificate.company_name}</p>
            </div>
            ` : ''}
            ${classData?.instructor_names ? `
            <div class="certificate-field">
              <label>Instructor(s)</label>
              <p>${classData.instructor_names}</p>
            </div>
            ` : ''}
            ${classData?.declaration_text ? `
            <div class="certificate-field">
              <label>Declaration</label>
              <p>${classData.declaration_text}</p>
            </div>
            ` : ''}
            ${classData?.extra_text ? `
            <div class="certificate-field">
              <label>Additional Information</label>
              <p>${classData.extra_text}</p>
            </div>
            ` : ''}
            <div style="display: flex; gap: 40px;">
              <div class="certificate-field" style="flex: 1;">
                <label>Issue Date</label>
                <p>${new Date(certificate.issue_date).toLocaleDateString('en-GB')}</p>
              </div>
              ${certificate.expiry_date ? `
              <div class="certificate-field" style="flex: 1;">
                <label>Expiry Date</label>
                <p>${new Date(certificate.expiry_date).toLocaleDateString('en-GB')}</p>
              </div>
              ` : ''}
            </div>
            ${classData?.signature_url ? `
            <div class="certificate-field" style="margin-top: 30px;">
              <label>Signature</label>
              <img src="${window.location.origin}${classData.signature_url}" style="max-height: 60px; margin-top: 10px;" />
            </div>
            ` : ''}
          </body>
        </html>
      `);
    }
    
    printWindow.document.close();
    
    // Wait for content to load before printing
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
    };
    
    // Fallback: try printing after a short delay if onload doesn't fire
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  const status = getCertificateStatus(certificate.expiry_date);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Certificate Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="p-3">
          <div className="text-center mb-4">
            <img src="/logo.png" alt="EMP Medic" className="h-8 mx-auto" />
            <p className="text-slate-500 text-xs mt-1">Training Certificate</p>
          </div>

          <div className="space-y-2">
            <div className="border-b pb-2">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Certificate Number</label>
              <p className="text-base font-mono text-[#2C9DE1]">{certificate.certificate_number}</p>
            </div>

            <div className="border-b pb-2">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Participant Name</label>
              <p className="text-sm font-semibold text-slate-900">{certificate.person_name}</p>
            </div>

            <div className="border-b pb-2">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Course</label>
              <p className="text-sm text-slate-900">{certificate.outcome_name || certificate.course_name}</p>
            </div>

            {certificate.company_name && (
              <div className="border-b pb-2">
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Company</label>
                <p className="text-sm text-slate-900">{certificate.company_name}</p>
              </div>
            )}

            {classData?.instructor_names && (
              <div className="border-b pb-2">
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Instructor(s)</label>
                <p className="text-sm text-slate-900">{classData.instructor_names}</p>
                {classData?.instructor_numbers && (
                  <p className="text-xs text-slate-500">#{classData.instructor_numbers}</p>
                )}
              </div>
            )}

            {classData?.declaration_text && (
              <div className="border-b pb-2">
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Declaration</label>
                <p className="text-slate-700 text-xs">{classData.declaration_text}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="border-b pb-2">
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Issue Date</label>
                <p className="text-sm text-slate-900">{formatDate(certificate.issue_date)}</p>
              </div>

              <div className="border-b pb-2">
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Expiry Date</label>
                <p className="text-sm text-slate-900">
                  {certificate.expiry_date ? formatDate(certificate.expiry_date) : <span className="text-slate-500">No expiration</span>}
                </p>
              </div>
            </div>

            <div className="mt-2">
              <Badge 
                className={`text-xs
                  ${!certificate.expiry_date ? 'bg-blue-100 text-blue-800' : ''}
                  ${certificate.expiry_date && status === 'active' ? 'bg-emerald-100 text-emerald-800' : ''}
                  ${status === 'expiring' ? 'bg-amber-100 text-amber-800' : ''}
                  ${status === 'expired' ? 'bg-red-100 text-red-800' : ''}
                `}
              >
                {!certificate.expiry_date ? 'Lifetime Valid' : status === 'active' ? 'Valid' : status === 'expiring' ? 'Expiring Soon' : 'Expired'}
              </Badge>
            </div>

            {/* Template indicator */}
            {template && (
              <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                <p className="text-xs text-blue-700 flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Template: <strong>{template.name}</strong> ({template.page_orientation})
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button 
            className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
            onClick={handlePrint}
          >
            <Printer className="w-4 h-4 mr-2" />
            {template ? 'Print with Template' : 'Print'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Certificates = () => {
  const { user } = useAuth();
  usePageTitle('Certificates');
  const [certificates, setCertificates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null); // For revoke confirmation dialog
  
  // Print template state
  const [printTemplate, setPrintTemplate] = useState(null);
  const [printClassData, setPrintClassData] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50;
  
  // Sort state
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Filter states
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [issueDatePeriod, setIssueDatePeriod] = useState('all');
  const [expiryDatePeriod, setExpiryDatePeriod] = useState('all');
  const [customIssueStart, setCustomIssueStart] = useState(null);
  const [customIssueEnd, setCustomIssueEnd] = useState(null);
  const [customExpiryStart, setCustomExpiryStart] = useState(null);
  const [customExpiryEnd, setCustomExpiryEnd] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);

  const canRevokeCertificates = user?.role === 'admin' || user?.role === 'training_admin';
  const canExport = user?.role === UserRoles.ADMIN;

  // Function to load print template and class data for a certificate
  const loadPrintData = async (certificate) => {
    try {
      // Find the course by name (since certificate may not have course_id)
      const course = courses.find(c => c.name === certificate.course_name);
      
      let template = null;
      let classData = null;
      
      // Fetch template if course has one
      if (course?.certificate_template_id) {
        try {
          const templateRes = await axios.get(`${API_URL}/certificate-templates/${course.certificate_template_id}`);
          template = templateRes.data;
        } catch (err) {
          console.error('Failed to fetch template:', err);
        }
      }
      
      // Fetch class data if certificate has class_id
      if (certificate.class_id) {
        try {
          const classRes = await axios.get(`${API_URL}/classes/${certificate.class_id}`);
          const cls = classRes.data;
          
          // Get instructor details
          if (cls.instructor_ids?.length > 0) {
            const instructorsRes = await axios.get(`${API_URL}/instructors`);
            const classInstructors = instructorsRes.data.filter(i => cls.instructor_ids.includes(i.id));
            // Pick primary instructor (or first) for certificate display
            const primaryId = cls.primary_instructor_id;
            const primary = (primaryId && classInstructors.find(i => i.id === primaryId)) || classInstructors[0];
            
            classData = {
              instructor_names: primary ? `${primary.first_name} ${primary.last_name}` : '',
              instructor_numbers: primary?.instructor_number || '',
              training_center_name: cls.training_center_name,
              duration_hours: cls.duration_hours,
              declaration_text: cls.declaration_text,
              extra_text: cls.extra_text,
              signature_url: cls.signature_url
            };
          } else {
            classData = {
              training_center_name: cls.training_center_name,
              duration_hours: cls.duration_hours,
              declaration_text: cls.declaration_text,
              extra_text: cls.extra_text,
              signature_url: cls.signature_url
            };
          }
        } catch (err) {
          console.error('Failed to fetch class data:', err);
        }
      }
      
      setPrintTemplate(template);
      setPrintClassData(classData);
      setSelectedCertificate(certificate);
    } catch (error) {
      console.error('Failed to load print data:', error);
      // Still show the certificate even if template loading fails
      setSelectedCertificate(certificate);
    }
  };

  const fetchData = async (page = 1, search = '', sortByArg = sortBy, sortOrderArg = sortOrder) => {
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (search) params.append('search', search);
      if (selectedCourse !== 'all') params.append('course_name', selectedCourse);
      if (sortByArg) {
        params.append('sort_by', sortByArg);
        params.append('sort_order', sortOrderArg);
      }
      
      const [certsRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/certificates?${params}`),
        axios.get(`${API_URL}/courses`)
      ]);
      
      // Handle paginated response
      const certsData = certsRes.data.items || certsRes.data;
      setCertificates(certsData);
      setTotalPages(certsRes.data.total_pages || 1);
      setTotalItems(certsRes.data.total || certsData.length);
      setCurrentPage(certsRes.data.page || 1);
      
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeCertificate = async (cert) => {
    // Show confirmation dialog instead of window.confirm
    setRevokeConfirm(cert);
  };

  const confirmRevokeCertificate = async () => {
    if (!revokeConfirm) return;
    
    const cert = revokeConfirm;
    setRevokeConfirm(null);
    setRevokingId(cert.id);
    
    try {
      await axios.delete(`${API_URL}/certificates/${cert.id}`);
      toast.success(`Certificate ${cert.certificate_number} revoked successfully`);
      fetchData(currentPage, searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke certificate');
    } finally {
      setRevokingId(null);
    }
  };

  useEffect(() => {
    fetchData(1, '');
  }, []);

  // Handle search and filter changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(1, searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedCourse]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    fetchData(newPage, searchTerm);
  };

  const handleSort = (field) => {
    let nextBy = field;
    let nextOrder = 'asc';
    if (sortBy === field) {
      if (sortOrder === 'asc') nextOrder = 'desc';
      else { nextBy = null; nextOrder = 'asc'; }
    }
    setSortBy(nextBy);
    setSortOrder(nextOrder);
    setLoading(true);
    fetchData(1, searchTerm, nextBy, nextOrder);
  };

  // Get date range based on period preset
  const getDateRange = (period, customStart, customEnd) => {
    const now = new Date();
    switch (period) {
      case 'this_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case 'this_year':
        return { start: startOfYear(now), end: endOfYear(now) };
      case 'last_year':
        const lastYear = new Date(now.getFullYear() - 1, 0, 1);
        return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
      case 'custom':
        return { start: customStart, end: customEnd };
      default:
        return null;
    }
  };

  // Check if date is within range
  const isDateInRange = (dateStr, range) => {
    if (!range || !range.start || !range.end) return true;
    try {
      const date = parseISO(dateStr);
      return isWithinInterval(date, { start: range.start, end: range.end });
    } catch {
      return true;
    }
  };

  // Filter certificates (date filters are client-side, search/course are server-side)
  const filteredCertificates = certificates.filter(cert => {
    // Issue date filter (client-side)
    const issueDateRange = getDateRange(issueDatePeriod, customIssueStart, customIssueEnd);
    const matchesIssueDate = isDateInRange(cert.issue_date, issueDateRange);
    
    // Expiry date filter (client-side)
    const expiryDateRange = getDateRange(expiryDatePeriod, customExpiryStart, customExpiryEnd);
    const matchesExpiryDate = isDateInRange(cert.expiry_date, expiryDateRange);
    
    return matchesIssueDate && matchesExpiryDate;
  });

  // Get unique course names from certificates
  const uniqueCourses = [...new Set(certificates.map(c => c.course_name))].filter(Boolean).sort();

  // Count active filters
  const activeFilterCount = [
    selectedCourse !== 'all',
    issueDatePeriod !== 'all',
    expiryDatePeriod !== 'all'
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSelectedCourse('all');
    setIssueDatePeriod('all');
    setExpiryDatePeriod('all');
    setCustomIssueStart(null);
    setCustomIssueEnd(null);
    setCustomExpiryStart(null);
    setCustomExpiryEnd(null);
  };

  // Export certificates to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      
      // Course filter
      if (selectedCourse !== 'all') {
        params.append('course_name', selectedCourse);
      }
      
      // Issue date range
      const issueDateRange = getDateRange(issueDatePeriod, customIssueStart, customIssueEnd);
      if (issueDateRange && issueDateRange.start) {
        params.append('issue_date_start', format(issueDateRange.start, 'yyyy-MM-dd'));
      }
      if (issueDateRange && issueDateRange.end) {
        params.append('issue_date_end', format(issueDateRange.end, 'yyyy-MM-dd'));
      }
      
      // Expiry date range
      const expiryDateRange = getDateRange(expiryDatePeriod, customExpiryStart, customExpiryEnd);
      if (expiryDateRange && expiryDateRange.start) {
        params.append('expiry_date_start', format(expiryDateRange.start, 'yyyy-MM-dd'));
      }
      if (expiryDateRange && expiryDateRange.end) {
        params.append('expiry_date_end', format(expiryDateRange.end, 'yyyy-MM-dd'));
      }
      
      const response = await axios.get(`${API_URL}/certificates/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'certificates_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${filteredCertificates.length} certificates to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export certificates');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="certificates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Certificates
          </h1>
          <p className="text-slate-500 mt-1">
            View and print training certificates
          </p>
        </div>
        {canExport && (
          <Button 
            variant="outline"
            onClick={handleExportCSV}
            disabled={exporting || filteredCertificates.length === 0}
            data-testid="export-certificates-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
        )}
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, course, certificate number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="certificate-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={activeFilterCount > 0 ? 'border-[#2C9DE1] text-[#2C9DE1]' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-[#2C9DE1] text-white">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="border-t pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-900">Filter Certificates</h3>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Clear all
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Course Filter */}
                <div className="space-y-2">
                  <Label>Course</Label>
                  <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Courses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courses</SelectItem>
                      {uniqueCourses.map((course) => (
                        <SelectItem key={course} value={course}>{course}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Issue Date Filter */}
                <div className="space-y-2">
                  <Label>Issue Date</Label>
                  <Select value={issueDatePeriod} onValueChange={setIssueDatePeriod}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="this_month">This Month</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="this_year">This Year</SelectItem>
                      <SelectItem value="last_year">Last Year</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                  {issueDatePeriod === 'custom' && (
                    <div className="flex gap-2 mt-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="flex-1 justify-start text-left font-normal">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {customIssueStart ? format(customIssueStart, 'dd MMM yyyy') : 'Start'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={customIssueStart} onSelect={setCustomIssueStart} />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="flex-1 justify-start text-left font-normal">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {customIssueEnd ? format(customIssueEnd, 'dd MMM yyyy') : 'End'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={customIssueEnd} onSelect={setCustomIssueEnd} />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>

                {/* Expiry Date Filter */}
                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Select value={expiryDatePeriod} onValueChange={setExpiryDatePeriod}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="this_month">This Month</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="this_year">This Year</SelectItem>
                      <SelectItem value="last_year">Last Year</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                  {expiryDatePeriod === 'custom' && (
                    <div className="flex gap-2 mt-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="flex-1 justify-start text-left font-normal">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {customExpiryStart ? format(customExpiryStart, 'dd MMM yyyy') : 'Start'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={customExpiryStart} onSelect={setCustomExpiryStart} />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="flex-1 justify-start text-left font-normal">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {customExpiryEnd ? format(customExpiryEnd, 'dd MMM yyyy') : 'End'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={customExpiryEnd} onSelect={setCustomExpiryEnd} />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
              </div>

              {/* Results count */}
              <div className="text-sm text-slate-500">
                Showing {filteredCertificates.length} of {certificates.length} certificates
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Certificates Table */}
      <Card>
        <CardContent className="p-0">
          {filteredCertificates.length === 0 ? (
            <div className="text-center py-12">
              <Award className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No certificates found</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm || activeFilterCount > 0 ? 'Try adjusting your filters' : 'Certificates will appear here after issuing'}
              </p>
              {activeFilterCount > 0 && (
                <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <SortableHeader field="certificate_number" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-cert-number">Certificate #</SortableHeader>
                  <SortableHeader field="person_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-cert-name">Name</SortableHeader>
                  <SortableHeader field="course_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-cert-course">Course</SortableHeader>
                  <TableHead className="font-semibold">Company</TableHead>
                  <SortableHeader field="issue_date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-cert-issue">Issue Date</SortableHeader>
                  <SortableHeader field="expiry_date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-cert-expiry">Expiry Date</SortableHeader>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCertificates.map((cert) => {
                  const status = getCertificateStatus(cert.expiry_date);
                  return (
                    <TableRow key={cert.id} className="hover:bg-slate-50/50" data-testid={`cert-row-${cert.id}`}>
                      <TableCell className="font-mono text-sm text-[#2C9DE1]">
                        {cert.certificate_number}
                      </TableCell>
                      <TableCell className="font-medium">{cert.person_name}</TableCell>
                      <TableCell>{cert.course_name}</TableCell>
                      <TableCell>{cert.company_name || '-'}</TableCell>
                      <TableCell>{formatDate(cert.issue_date)}</TableCell>
                      <TableCell>{formatDate(cert.expiry_date)}</TableCell>
                      <TableCell>
                        <Badge 
                          className={`
                            ${status === 'active' ? 'bg-emerald-100 text-emerald-800' : ''}
                            ${status === 'expiring' ? 'bg-amber-100 text-amber-800' : ''}
                            ${status === 'expired' ? 'bg-red-100 text-red-800' : ''}
                          `}
                        >
                          {status === 'active' ? 'Active' : status === 'expiring' ? 'Expiring' : 'Expired'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`cert-actions-${cert.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => loadPrintData(cert)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View & Print
                            </DropdownMenuItem>
                            {canRevokeCertificates && (
                              <DropdownMenuItem 
                                onClick={() => handleRevokeCertificate(cert)}
                                className="text-red-600 focus:text-red-600"
                                disabled={revokingId === cert.id}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {revokingId === cert.id ? 'Revoking...' : 'Revoke Certificate'}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-slate-500">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems} certificates
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Print View Dialog */}
      {selectedCertificate && (
        <CertificatePrintView 
          certificate={selectedCertificate}
          template={printTemplate}
          classData={printClassData}
          onClose={() => {
            setSelectedCertificate(null);
            setPrintTemplate(null);
            setPrintClassData(null);
          }} 
        />
      )}

      {/* Revoke Certificate Confirmation Dialog */}
      {revokeConfirm && (
        <Dialog open={true} onOpenChange={() => setRevokeConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Revoke Certificate
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to revoke certificate <strong>{revokeConfirm.certificate_number}</strong> for <strong>{revokeConfirm.person_name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setRevokeConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmRevokeCertificate}
              >
                Revoke
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Certificates;
